import * as React from "react";
import { View, Linking } from "react-native";
import { Text, useDripsyTheme } from "dripsy";

interface TextMark {
  type: "bold" | "italic" | "underline" | "strike" | "link";
  attrs?: {
    href?: string;
    target?: string;
  };
}

interface TextNode {
  type: "text";
  text: string;
  marks?: TextMark[];
}

interface ParagraphNode {
  type: "paragraph";
  content?: TextNode[];
}

interface HeadingNode {
  type: "heading";
  attrs: {
    level: 1 | 2 | 3 | 4 | 5 | 6;
  };
  content?: TextNode[];
}

interface ListItemNode {
  type: "list_item";
  content?: ParagraphNode[];
}

interface BulletListNode {
  type: "bullet_list";
  content?: ListItemNode[];
}

interface OrderedListNode {
  type: "ordered_list";
  content?: ListItemNode[];
}

type ContentNode =
  | ParagraphNode
  | HeadingNode
  | BulletListNode
  | OrderedListNode;

interface RichTextContent {
  type: string;
  content: ContentNode[];
}

function renderTextWithMarks(
  textNode: TextNode,
  key: string,
  colors: { text: string; primary: string }
) {
  let content: React.ReactNode = textNode.text;

  if (textNode.marks) {
    for (const mark of textNode.marks) {
      if (mark.type === "bold") {
        content = (
          <Text key={`${key}-bold`} style={{ fontWeight: "bold" }}>
            {content}
          </Text>
        );
      } else if (mark.type === "italic") {
        content = (
          <Text key={`${key}-italic`} style={{ fontStyle: "italic" }}>
            {content}
          </Text>
        );
      } else if (mark.type === "underline") {
        content = (
          <Text
            key={`${key}-underline`}
            style={{ textDecorationLine: "underline" }}
          >
            {content}
          </Text>
        );
      } else if (mark.type === "link" && mark.attrs?.href) {
        const href = mark.attrs.href;
        content = (
          <Text
            key={`${key}-link`}
            style={{ color: colors.primary, textDecorationLine: "underline" }}
            onPress={() => Linking.openURL(href)}
          >
            {content}
          </Text>
        );
      }
    }
  }

  return <React.Fragment key={key}>{content}</React.Fragment>;
}

function renderParagraphContent(
  content: TextNode[] | undefined,
  colors: { text: string; primary: string }
) {
  if (!content) return null;
  return content.map((textNode, idx) =>
    renderTextWithMarks(textNode, `text-${idx}`, colors)
  );
}

function ListItem({
  node,
  index,
  ordered,
  colors,
}: {
  node: ListItemNode;
  index: number;
  ordered?: boolean;
  colors: { text: string; primary: string };
}) {
  const bullet = ordered ? `${index + 1}.` : "•";

  return (
    <View style={{ flexDirection: "row", marginBottom: 6 }}>
      <Text style={{ fontSize: 16, lineHeight: 24, width: 20, color: colors.text }}>
        {bullet}
      </Text>
      <View style={{ flex: 1 }}>
        {node.content?.map((paragraph, pIdx) => (
          <Text
            key={`li-${index}-p-${pIdx}`}
            style={{ fontSize: 16, lineHeight: 24, color: colors.text }}
          >
            {renderParagraphContent(paragraph.content, colors)}
          </Text>
        ))}
      </View>
    </View>
  );
}

export default function StoryblokRichtext({
  content,
}: {
  content: RichTextContent | any | undefined;
}) {
  const { theme } = useDripsyTheme();
  const colors = {
    text: theme.colors.$text as string,
    primary: theme.colors.$primary as string,
  };

  if (!content?.content) return null;

  return (
    <>
      {content.content.map((node: any, index: number) => {
        if (node.type === "heading") {
          const headingNode = node as HeadingNode;
          const level = headingNode.attrs?.level || 2;
          const fontSize = level === 1 ? 28 : level === 2 ? 24 : 18;

          return (
            <Text
              key={`heading-${index}`}
              style={{
                fontWeight: "bold",
                fontSize,
                marginTop: 12,
                marginBottom: 12,
                color: colors.text,
              }}
            >
              {renderParagraphContent(headingNode.content, colors)}
            </Text>
          );
        }

        if (node.type === "paragraph") {
          const paragraphNode = node as ParagraphNode;
          if (!paragraphNode.content?.length) {
            return <View key={`empty-${index}`} style={{ height: 12 }} />;
          }

          return (
            <Text
              key={`paragraph-${index}`}
              style={{
                fontSize: 16,
                lineHeight: 24,
                marginBottom: 12,
                color: colors.text,
              }}
            >
              {renderParagraphContent(paragraphNode.content, colors)}
            </Text>
          );
        }

        if (node.type === "bullet_list") {
          const listNode = node as BulletListNode;
          return (
            <View key={`bullet-list-${index}`} style={{ marginBottom: 12 }}>
              {listNode.content?.map((listItem, liIndex) => (
                <ListItem
                  key={`li-${index}-${liIndex}`}
                  node={listItem}
                  index={liIndex}
                  colors={colors}
                />
              ))}
            </View>
          );
        }

        if (node.type === "ordered_list") {
          const listNode = node as OrderedListNode;
          return (
            <View key={`ordered-list-${index}`} style={{ marginBottom: 12 }}>
              {listNode.content?.map((listItem, liIndex) => (
                <ListItem
                  key={`li-${index}-${liIndex}`}
                  node={listItem}
                  index={liIndex}
                  ordered
                  colors={colors}
                />
              ))}
            </View>
          );
        }

        return null;
      })}
    </>
  );
}
